import React, { useState } from "react";
import ConfirmableActionButton from "../../common/component/ConfirmableActionTextButton";
import { Path } from "../utils/PathUtils";
import { buildBundles, buildImage, buildMessageBundle } from "../utils/Utils";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { ThemeProvider } from "@mui/material";
import ClearableTextField from "./ClearableTextField";
import ClearableSpeechLabel from "./ClearableSpeechLabel";
import SpeechTextField from "./SpeechTextField";
import CoverImagePicker from "./CoverImagePicker";
import ChromaImageView from "./ChromaImageView";
import AdditionalImagePicker from "./AdditionalImagePicker";
import RetailPeriodView from "./RetailPeriodView";
import GroupItemDisplayView from "./GroupItemDisplayView";
import { InputTheme } from "../../common/utils/ThemeUtils";
import { v4 as uuidv4 } from "uuid";
import styles from "../style/resource.module.css";

const GroupContentView = (props) => {
  const [name, setName] = useState(props.element?.name?.default);
  const [nameLocales, setNameLocales] = useState(
    buildBundles(props.element?.name?.bundles) || []
  );
  const [description, setDescription] = useState(
    props.element?.description?.default
  );
  const [descriptionLocales, setDescriptionLocales] = useState(
    buildBundles(props.element?.description?.bundles) || []
  );
  const [cover, setCover] = useState(props.element?.image?.cover);
  const [images, setImages] = useState(props.element?.image?.images || []);
  const [startRetailDate, setStartRetailDate] = useState(
    props.element?.startRetailDate
  );
  const [endRetailDate, setEndRetailDate] = useState(
    props.element?.endRetailDate
  );
  const [items, setItems] = useState(props.element?.items || []);

  const navigate = useNavigate();

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <label className={styles.label}>Resources</label>
        <div className={styles.headerControls}>
          <ConfirmableActionButton
            className={styles.button}
            title="abort"
            message="Are you sure you want to discard the changes? Unsaved changes will be lost."
            onClick={() => navigate(Path.RESOURCE)}
          />
          <ConfirmableActionButton
            className={styles.button}
            title="save"
            message="Are you sure you want to proceed with saving the changes?"
            onClick={() => {
              props.onClick({
                name: buildMessageBundle(name, nameLocales),
                description: buildMessageBundle(
                  description,
                  descriptionLocales
                ),
                image: buildImage(cover, images),
                startRetailDate: startRetailDate,
                endRetailDate: endRetailDate,
                items: items,
              });
            }}
          />
        </div>
      </div>
      <Grid
        container
        spacing={3}
        sx={{ overflowY: "scroll" }}
        className={styles.content}
      >
        <ThemeProvider theme={InputTheme}>
          <Grid sx={{ flex: 1 }} key={"group-general-column"}>
            <div className={styles.component}>
              <label className={styles.label}>GENERAL</label>
              {/*group name section*/}
              <label className={styles.text}>Group Name</label>
              <ClearableTextField
                id="default-name-input"
                label="Default Group Name"
                value={name}
                onChange={setName}
              />
              {nameLocales.map((locale) => {
                return (
                  <ClearableSpeechLabel
                    speech={locale.speech}
                    text={locale.value}
                    onRemove={() =>
                      setNameLocales(
                        nameLocales.filter(
                          (nameLocale) => nameLocale.speech !== locale.speech
                        )
                      )
                    }
                  />
                );
              })}
              <SpeechTextField
                id="locale-name-input"
                onChange={(value) => {
                  if (
                    !nameLocales.some(
                      (locale) => locale.speech === value.speech
                    )
                  ) {
                    setNameLocales([value, ...nameLocales]);
                  }
                }}
              />
              {/*group description section*/}
              <label className={styles.text}>Group Description</label>
              <ClearableTextField
                id="default-description-input"
                label="Default Group Description"
                multiline={true}
                value={description}
                onChange={setDescription}
              />
              {descriptionLocales.map((locale) => {
                return (
                  <ClearableSpeechLabel
                    speech={locale.speech}
                    text={locale.value}
                    multiline={true}
                    onRemove={() =>
                      setDescriptionLocales(
                        descriptionLocales.filter(
                          (descLocale) => descLocale.speech !== locale.speech
                        )
                      )
                    }
                  />
                );
              })}
              <SpeechTextField
                id="locale-description-input"
                multiline={true}
                onChange={(value) => {
                  if (
                    !descriptionLocales.some(
                      (locale) => locale.speech === value.speech
                    )
                  ) {
                    setDescriptionLocales([value, ...descriptionLocales]);
                  }
                }}
              />
              {/*group media section*/}
              <label className={styles.text}>Group Media</label>
              <div style={{ display: "flex", marginTop: "2vh" }}>
                <CoverImagePicker
                  src={cover}
                  onSelect={setCover}
                  onRemove={() => setCover(null)}
                />
                <Grid container spacing={1}>
                  {images.map((image, index) => {
                    return [
                      <Grid key={`additional-image-${index}`}>
                        <ChromaImageView
                          src={image}
                          defaultColor="#EEEEEE"
                          size="8vw"
                          onRemove={() =>
                            setImages(images.filter((src) => src !== image))
                          }
                        />
                      </Grid>,
                    ];
                  })}
                  <Grid size={15} key={"additional-image-picker"}>
                    <AdditionalImagePicker
                      onSelect={(image) => setImages([...images, image])}
                    />
                  </Grid>
                </Grid>
              </div>
              {/*group retail period section*/}
              <label className={styles.text}>Group Retail Period</label>
              <RetailPeriodView
                startRetailDate={startRetailDate}
                endRetailDate={endRetailDate}
                onStartRetailDateSelect={setStartRetailDate}
                onEndRetailDateSelect={setEndRetailDate}
              />
            </div>
          </Grid>
          <Grid size={7} key={"group-entities-column"}>
            <div className={styles.component}>
              <label className={styles.label}>ENTITIES</label>
              <GroupItemDisplayView
                items={items}
                onCreate={(item) => {
                  if (item.uuid === undefined) {
                    item.uuid = uuidv4();
                  }
                  setItems([item, ...items]);
                }}
                onUpdate={(item) => {
                  const actualItems = [];
                  items.forEach((actual) => {
                    if (actual.uuid === item.uuid) {
                      actualItems.push(item);
                    } else {
                      actualItems.push(actual);
                    }
                  });
                  setItems(actualItems);
                }}
                onRemove={(item) =>
                  setItems(items.filter((it) => it.uuid !== item.uuid))
                }
              />
            </div>
          </Grid>
        </ThemeProvider>
      </Grid>
    </div>
  );
};

export default GroupContentView;
